/**
 * A hook for handling errors and displaying messages using useNotification.
 * Supports errors in the form of an array of strings, an object, or a single string.
 *
 * - If the error is an array, each element of the array will be displayed as a separate message.
 * - If the error is an object, each value in the object will be displayed as a separate message.
 * - If the error doesn't match the above formats, a generic error message will be shown.
 *
 * @returns {Function} showError - A function to process and display error messages.
 */
import { useCallback } from 'react';

import useNotification from 'hooks/useNotification';

type ErrorType = string | string[] | Record<string, string>;

const useErrorHandler = () => {
	const showNotification = useNotification();

	const showError = useCallback(
		(error: ErrorType) => {
			if (Array.isArray(error)) {
				error.forEach((err) => showNotification('error', err));
			} else if (typeof error === 'object' && error !== null) {
				Object.values(error).forEach((err) => showNotification('error', err));
			} else if (typeof error === 'string') {
				showNotification('error', error);
			} else {
				showNotification('error', 'Something went wrong');
			}

			if (error) {
				console.error(error);
			}
		},
		[showNotification],
	);

	return showError;
};

export default useErrorHandler;
